import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RotateCcw, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/common";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { money, dateTime, fmtClabe, TX_LABEL } from "@/lib/format";

const Row = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm"><span className="text-slate-500">{k}</span><span className={`text-right text-slate-900 ${mono ? "font-mono" : ""}`}>{v}</span></div>
);

export const TxDetail = ({ tx, onClose, onChanged }) => {
  const api = useApi();
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const devolver = async () => {
    setBusy(true);
    try {
      await api.patch(`/transactions/${tx.id}/status`, { estado: "devuelta" });
      toast.success("Devolución registrada");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setAsk(false); }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => { if (!o) { setAsk(false); onClose(); } }}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="tx-detail-modal">
        <DialogHeader><DialogTitle>Detalle SPEI</DialogTitle></DialogHeader>
        {tx && (
          <div>
            <p className={`font-display text-3xl font-semibold ${tx.tipo === "entrada" ? "text-teal-700" : "text-slate-900"}`}>{tx.tipo === "entrada" ? "+" : "-"}{money(tx.monto)}</p>
            <div className="mt-2 mb-4"><TxBadge t={tx} /></div>
            <Row k="Clave de rastreo" v={tx.clave_rastreo} mono />
            <Row k="ID STP" v={tx.stp_id} mono />
            <Row k="Fecha" v={dateTime(tx.created_at)} />
            <Row k="Cliente e-Plata" v={tx.client_nombre} />
            <Row k="CLABE e-Plata" v={fmtClabe(tx.clabe)} mono />
            <Row k={tx.tipo === "entrada" ? "Ordenante" : "Beneficiario"} v={tx.contraparte_nombre} />
            <Row k="CLABE contraparte" v={fmtClabe(tx.contraparte_clabe)} mono />
            <Row k="Banco" v={tx.contraparte_banco} />
            <Row k="Concepto" v={tx.concepto} />
            <Row k="Referencia" v={tx.referencia_numerica} mono />
            {tx.folio_banxico && <Row k="Folio Banco de México" v={tx.folio_banxico} mono />}
            {tx.folio && <Row k="Folio estado de cuenta" v={tx.folio} mono />}
            <Row k="Saldo después" v={money(tx.saldo_despues)} mono />
            <Button className="mt-5 w-full bg-teal-600 hover:bg-teal-700" data-testid="tx-receipt-btn"
              onClick={() => downloadBlob(api, `/transactions/${tx.id}/receipt`, `Comprobante_${tx.clave_rastreo}.pdf`).catch((e) => toast.error(errMsg(e)))}>
              <FileDown className="mr-2 h-4 w-4" />Descargar comprobante
            </Button>
            {tx.estado === "liquidada" && !tx.devuelta && !tx.es_devolucion && !tx.import_tag && (
              ask ? (
                <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <p>Se registrará un movimiento de devolución por {money(tx.monto)}{tx.orden_id ? " en ambas cuentas e-Plata" : ""}.</p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAsk(false)}>Cancelar</Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" disabled={busy} onClick={devolver} data-testid="tx-devolver-confirm">{busy ? "Procesando…" : "Confirmar devolución"}</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="mt-5 w-full text-red-700 hover:bg-red-50" onClick={() => setAsk(true)} data-testid="tx-devolver-btn"><RotateCcw className="mr-2 h-4 w-4" />Devolver transacción</Button>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TxBadge = ({ t }) => {
  if (t.devuelta) return <StatusBadge status="devuelta" label="Devuelta" />;
  if (t.es_devolucion) return <StatusBadge status="pendiente" label="Devolución" />;
  return <StatusBadge status={t.estado} label={TX_LABEL[t.estado]} />;
};

const toCsv = (rows) => {
  const head = ["Fecha", "Cliente", "CLABE", "Tipo", "Monto", "Contraparte", "CLABE contraparte", "Banco", "Concepto", "Clave rastreo", "Estado"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((t) => [dateTime(t.created_at), t.client_nombre, `'${t.clabe}`, t.tipo, t.monto, t.contraparte_nombre, `'${t.contraparte_clabe}`, t.contraparte_banco, t.concepto, t.clave_rastreo, t.devuelta ? "devuelta" : t.es_devolucion ? "devolucion" : t.estado].map(esc).join(","));
  const blob = new Blob(["\ufeff" + [head.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "transacciones_eplata.csv";
  a.click();
};

export const TxTable = ({ params, showClient = true, reloadKey = 0 }) => {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [tick, setTick] = useState(0);
  const key = JSON.stringify(params);
  useEffect(() => {
    api.get("/transactions", { params: JSON.parse(key) }).then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(errMsg(e)); });
  }, [api, key, reloadKey, tick]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <p className="text-sm text-slate-500" data-testid="tx-count">{rows ? (rows.length >= 500 ? "Mostrando los 500 más recientes · usa las fechas para ver otros periodos" : `${rows.length} movimientos`) : "Cargando…"}</p>
        <Button size="sm" variant="outline" disabled={!rows?.length} onClick={() => toCsv(rows)} data-testid="tx-export-csv"><Download className="mr-2 h-4 w-4" />Exportar CSV</Button>
      </div>
      <div className="max-h-[640px] overflow-auto">
        <table className="w-full min-w-[900px] text-sm" data-testid="tx-table">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Fecha</th>{showClient && <th className="px-4 py-3">Cliente</th>}<th className="px-4 py-3">Contraparte</th><th className="px-4 py-3">Concepto</th><th className="px-4 py-3">Clave de rastreo</th><th className="px-4 py-3 text-right">Monto</th><th className="px-4 py-3">Estado</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows?.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Sin movimientos</td></tr>}
            {rows?.map((t) => (
              <tr key={t.id} onClick={() => setSel(t)} data-testid={`tx-row-${t.id}`} className="cursor-pointer transition-colors hover:bg-teal-50/40">
                <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{dateTime(t.created_at)}</td>
                {showClient && <td className="px-4 py-3.5 font-medium text-slate-900">{t.client_nombre}</td>}
                <td className="px-4 py-3.5"><p className="text-slate-900">{t.contraparte_nombre}</p><p className="text-xs text-slate-500">{t.contraparte_banco}</p></td>
                <td className="px-4 py-3.5 text-slate-600">{t.concepto}</td>
                <td className="px-4 py-3.5 font-mono text-xs text-slate-600">{t.clave_rastreo}</td>
                <td className={`whitespace-nowrap px-4 py-3.5 text-right font-mono ${t.tipo === "entrada" ? "text-teal-700" : "text-slate-900"}`}>{t.tipo === "entrada" ? "+" : "-"}{money(t.monto)}</td>
                <td className="px-4 py-3.5"><TxBadge t={t} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TxDetail tx={sel} onClose={() => setSel(null)} onChanged={() => setTick(tick + 1)} />
    </div>
  );
};
